import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { FormSkeleton } from "../../components/ui/FormSkeleton";
import { PageBuilder } from "../../components/pageBuilder/PageBuilder";
import { PageBuilderProvider } from "../../context/PageBuilderContext";
import { IComponentNode } from "../../types/pageBuilder.types";
import { FaEdit, FaTimes, FaGripVertical, FaArrowUp, FaArrowDown, FaUndo, FaTrash } from "react-icons/fa";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Spinner } from "components/ui";

const PlatformPageForm: React.FC = () => {
 const { t } = useTranslation();
 const { slug } = useParams<{ slug: string }>();
 const navigate = useNavigate();
 const { isAuthenticated } = usePlatformAuth();
 const [initialLoading, setInitialLoading] = useState(!!(slug && slug !== "new"));
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [formData, setFormData] = useState({
  title: "",
  slug: "",
  isPublished: false,
  metaTitle: "",
  metaDescription: "",
 });
 const [layout, setLayout] = useState<IComponentNode | null>(null);
 const [showPageBuilder, setShowPageBuilder] = useState(false);
 const [zones, setZones] = useState<Record<string, IComponentNode>>({});
 const [editingZone, setEditingZone] = useState<{ name: string; content: IComponentNode } | null>(null);
 const [zoneNames, setZoneNames] = useState<string[]>([]);
 const [showResetConfirm, setShowResetConfirm] = useState(false);
 const [resettingZones, setResettingZones] = useState(false);

 // Check if this is a policy page (uses layout AND zones)
 // Includes both base policy pages (privacy, cookie, terms, etc.) and policy detail pages
 const isPolicyPage = (slug: string | undefined): boolean => {
  if (!slug) return false;
  const basePolicySlugs = ["privacy", "cookie", "terms", "dpa", "acceptable-use", "gdpr-rights"];
  if (basePolicySlugs.includes(slug)) return true;
  // Check if slug contains policy-related keywords
  const policyPatterns = ["privacy", "cookie", "terms", "dpa", "acceptable-use", "gdpr-rights", "policy"];
  const slugLower = slug.toLowerCase();
  return policyPatterns.some(pattern => slugLower.includes(pattern));
 };

 // Check if this is a predefined page (has default zones)
 const isPredefinedPage = (slug: string | undefined): boolean => {
  if (!slug) return false;
  const predefinedPages = ["home", "how-it-works", "pricing", "contact", "onboarding", "policies"];
  return predefinedPages.includes(slug);
 };

 // Check if a page has zones (for zone management UI)
 const hasZones = (): boolean => {
  return zoneNames.length > 0 || Object.keys(zones).length > 0;
 };

 // Check if this is a new page
 const isNewPage = !slug || slug === "new";

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (slug && slug !== "new") {
   fetchPage();
  }
 }, [slug, isAuthenticated, navigate]);

 const fetchPage = async () => {
  try {
   setInitialLoading(true);
   setError(null);
   const response = await platformApiService.getPlatformPage(slug!);
   const page = response.data.page;
   if (page) {
    setFormData({
     title: page.title || "",
     slug: page.slug || "",
     isPublished: page.isPublished !== undefined ? page.isPublished : false,
     metaTitle: page.metaTitle || "",
     metaDescription: page.metaDescription || "",
    });
    
    // Policy pages have both layout AND zones
    if (isPolicyPage(page.slug)) {
     setLayout(page.layout || null);
     // Load zones for policy pages (top and bottom)
     if (page.zones) {
      setZones(page.zones);
      if (page.zoneNames && page.zoneNames.length > 0) {
       setZoneNames(page.zoneNames);
      } else {
       setZoneNames(Object.keys(page.zones));
      }
     } else {
      // Initialize default zones for policy pages
      setZoneNames(["top", "bottom"]);
     }
    } else if (isPredefinedPage(page.slug)) {
     // Predefined pages use zones
     if (page.zones) {
      setZones(page.zones);
      // Use zoneNames if available, otherwise fall back to Object.keys
      if (page.zoneNames && page.zoneNames.length > 0) {
       setZoneNames(page.zoneNames);
      } else {
       setZoneNames(Object.keys(page.zones));
      }
     } else {
      // Auto-detect zones based on page slug
      const defaultZones = getDefaultZonesForPage(page.slug);
      setZoneNames(defaultZones);
     }
    } else {
     // Non-predefined pages use layout (page builder)
     // But they might also have zones if they were created with zones
     setLayout(page.layout || null);
     if (page.zones) {
      setZones(page.zones);
      if (page.zoneNames && page.zoneNames.length > 0) {
       setZoneNames(page.zoneNames);
      } else {
       setZoneNames(Object.keys(page.zones));
      }
     }
    }
   }
  } catch (err: any) {
   console.error("Error fetching page:", err);
   setError(err.response?.data?.error || "Failed to fetch page");
  } finally {
   setInitialLoading(false);
  }
 };

 const generateSlug = (title: string) => {
  return title
   .toLowerCase()
   .trim()
   .replace(/[^\w\s-]/g, "")
   .replace(/[\s_-]+/g, "-")
   .replace(/^-+|-+$/g, "");
 };

 const getDefaultZonesForPage = (slug: string): string[] => {
  const zoneMap: Record<string, string[]> = {
   home: ["hero", "features", "benefits", "stats", "cta"],
   "how-it-works": ["hero", "steps", "features", "cta"],
   pricing: ["hero", "plans", "faq", "cta"],
   contact: ["hero", "contact-info", "business-hours"],
   onboarding: ["header", "info"],
   policies: ["hero", "info", "policies", "questions", "cta"],
  };
  
  // Check if it's a policy page (base or detail)
  if (isPolicyPage(slug)) {
   return ["top", "bottom"];
  }
  
  return zoneMap[slug] || ["content"];
 };

 const handleEditZone = (zoneName: string) => {
  const zoneContent = zones[zoneName] || {
   id: `zone-${zoneName}`,
   type: "container",
   props: {},
   styles: { default: {} },
   children: [],
  };
  setEditingZone({ name: zoneName, content: zoneContent });
 };

 const handleSaveZone = async (zoneName: string, layout: IComponentNode) => {
  if (!slug || slug === "new") {
   // If page doesn't exist yet, just update local state
   const updatedZones = { ...zones, [zoneName]: layout };
   setZones(updatedZones);
   setEditingZone(null);
   return;
  }

  // Save zone directly to database
  try {
   const updatedZones = { ...zones, [zoneName]: layout };
   await platformApiService.updatePlatformPage(slug, {
    ...formData,
    zones: updatedZones,
   });
   setZones(updatedZones);
   setEditingZone(null);
  } catch (error: any) {
   console.error("Error saving zone:", error);
   alert(error.response?.data?.error || "Failed to save zone");
  }
 };

 const handleCancelZoneEdit = () => {
  setEditingZone(null);
 };

 const handleZoneOrderChange = (event: DragEndEvent) => {
  const { active, over } = event;
  
  if (!over || active.id === over.id) return;
  
  const oldIndex = zoneNames.indexOf(active.id as string);
  const newIndex = zoneNames.indexOf(over.id as string);
  
  if (oldIndex !== -1 && newIndex !== -1) {
   const newZoneNames = arrayMove(zoneNames, oldIndex, newIndex);
   setZoneNames(newZoneNames);
   
   // Reorder zones object to match new order
   const reorderedZones: Record<string, IComponentNode> = {};
   newZoneNames.forEach((zoneName) => {
    if (zones[zoneName]) {
     reorderedZones[zoneName] = zones[zoneName];
    }
   });
   setZones(reorderedZones);
  }
 };

 const handleMoveZone = (zoneName: string, direction: "up" | "down") => {
  const currentIndex = zoneNames.indexOf(zoneName);
  if (currentIndex === -1) return;
  
  const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
  if (newIndex < 0 || newIndex >= zoneNames.length) return;
  
  const newZoneNames = arrayMove(zoneNames, currentIndex, newIndex);
  setZoneNames(newZoneNames);
  
  // Reorder zones object to match new order
  const reorderedZones: Record<string, IComponentNode> = {};
  newZoneNames.forEach((name) => {
   if (zones[name]) {
    reorderedZones[name] = zones[name];
   }
  });
  setZones(reorderedZones);
 };

 const handleDeleteZone = async (zoneName: string) => {
  if (!window.confirm(`Are you sure you want to delete the "${zoneName}" zone? This action cannot be undone.`)) {
   return;
  }

  try {
   setError(null);
   if (slug && slug !== "new") {
    // Delete from backend
    await platformApiService.deletePlatformZone(slug, zoneName);
   }
   
   // Remove from local state
   const updatedZones = { ...zones };
   delete updatedZones[zoneName];
   setZones(updatedZones);
   
   const updatedZoneNames = zoneNames.filter((name) => name !== zoneName);
   setZoneNames(updatedZoneNames);
   
   // If we were editing this zone, close the editor
   if (editingZone?.name === zoneName) {
    setEditingZone(null);
   }
  } catch (err: any) {
   const errorMessage = err.response?.data?.error || err.message || "Failed to delete zone";
   setError(errorMessage);
   console.error("Error deleting zone:", err);
  }
 };

 const handleTitleChange = (title: string) => {
  setFormData((prev) => ({
   ...prev,
   title,
   slug: prev.slug || generateSlug(title),
  }));
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  setSaving(true);

  try {
   const pageData: any = {
    title: formData.title,
    // Don't send slug - backend will auto-generate from title
    isPublished: formData.isPublished,
    metaTitle: formData.metaTitle || undefined,
    metaDescription: formData.metaDescription || undefined,
   };

   // Policy pages have both layout AND zones
   // Predefined pages use zones
   // New/custom pages use layout (but may also have zones)
   const generatedSlug = slug || (formData.title ? generateSlug(formData.title) : "");
   
   if (isPolicyPage(slug || generatedSlug)) {
    // Policy pages: save both layout and zones
    pageData.layout = layout || undefined;
    if (zoneNames.length > 0 || Object.keys(zones).length > 0) {
     const orderedZones: Record<string, IComponentNode> = {};
     zoneNames.forEach((zoneName) => {
      if (zones[zoneName]) {
       orderedZones[zoneName] = zones[zoneName];
      }
     });
     pageData.zones = orderedZones;
     pageData.zoneNames = zoneNames;
    }
   } else if (isPredefinedPage(slug || generatedSlug)) {
    // Predefined pages: save zones
    if (zoneNames.length > 0) {
     const orderedZones: Record<string, IComponentNode> = {};
     zoneNames.forEach((zoneName) => {
      if (zones[zoneName]) {
       orderedZones[zoneName] = zones[zoneName];
      }
     });
     pageData.zones = orderedZones;
     pageData.zoneNames = zoneNames; // Save zone order explicitly
    } else {
     pageData.zones = Object.keys(zones).length > 0 ? zones : undefined;
     pageData.zoneNames = Object.keys(zones).length > 0 ? Object.keys(zones) : undefined;
    }
   } else {
    // New or custom pages: save layout and zones if they exist
    pageData.layout = layout || undefined;
    if (zoneNames.length > 0 || Object.keys(zones).length > 0) {
     const orderedZones: Record<string, IComponentNode> = {};
     zoneNames.forEach((zoneName) => {
      if (zones[zoneName]) {
       orderedZones[zoneName] = zones[zoneName];
      }
     });
     pageData.zones = Object.keys(orderedZones).length > 0 ? orderedZones : undefined;
     pageData.zoneNames = zoneNames.length > 0 ? zoneNames : undefined;
    }
   }
   
   if (slug && slug !== "new") {
    // Update existing page
    await platformApiService.updatePlatformPage(slug, pageData);
   } else {
    // Create new page - slug will be auto-generated from title on backend
    const generatedSlug = generateSlug(formData.title);
    if (isPolicyPage(generatedSlug)) {
     // Policy pages: create with layout and default zones (top, bottom)
     const initialZones: Record<string, IComponentNode> = {};
     ["top", "bottom"].forEach((zoneName) => {
      initialZones[zoneName] = {
       id: `zone-${zoneName}`,
       type: "container",
       props: {},
       styles: { default: {} },
       children: [],
      };
     });
     await platformApiService.upsertPlatformPage({
      ...pageData,
      layout: layout || undefined,
      zones: initialZones,
      zoneNames: ["top", "bottom"],
     });
    } else if (isPredefinedPage(generatedSlug)) {
     // Predefined pages: create with default zones
     const defaultZones = getDefaultZonesForPage(generatedSlug);
     const initialZones: Record<string, IComponentNode> = {};
     defaultZones.forEach((zoneName) => {
      initialZones[zoneName] = {
       id: `zone-${zoneName}`,
       type: "container",
       props: {},
       styles: { default: {} },
       children: [],
      };
     });
     await platformApiService.upsertPlatformPage({
      ...pageData,
      zones: initialZones,
      zoneNames: defaultZones,
     });
    } else {
     // New/custom pages: use layout (page builder), but can also have zones
     const initialZones: Record<string, IComponentNode> = {};
     if (zoneNames.length > 0) {
      zoneNames.forEach((zoneName) => {
       if (zones[zoneName]) {
        initialZones[zoneName] = zones[zoneName];
       } else {
        initialZones[zoneName] = {
         id: `zone-${zoneName}`,
         type: "container",
         props: {},
         styles: { default: {} },
         children: [],
        };
       }
      });
     }
     await platformApiService.upsertPlatformPage({
      ...pageData,
      layout: layout || undefined,
      zones: Object.keys(initialZones).length > 0 ? initialZones : undefined,
      zoneNames: zoneNames.length > 0 ? zoneNames : undefined,
     });
    }
   }
   navigate("/platform/pages");
  } catch (err: any) {
   console.error("Error saving page:", err);
   setError(err.response?.data?.error || "Failed to save page");
  } finally {
   setSaving(false);
  }
 };

 const handleResetZones = async () => {
  if (!slug || slug === "new") {
   return;
  }

  try {
   setResettingZones(true);
   setError(null);
   const response = await platformApiService.resetAllZonesForPage(slug);
   setShowResetConfirm(false);
   
   // Refresh page data to show updated zones
   await fetchPage();
   
   alert(response.data.message || "Zones reset successfully!");
  } catch (err: any) {
   console.error("Error resetting zones:", err);
   setError(err.response?.data?.error || "Failed to reset zones");
  } finally {
   setResettingZones(false);
  }
 };

 if (initialLoading) {
  return (
   <div className="p-8">
    <div className="mb-6 flex justify-between items-center">
     <h1 className="text-3xl font-bold">
      {slug && slug !== "new" ? "Edit Page" : "Create New Page"}
     </h1>
     <button
      onClick={() => navigate("/platform/pages")}
      className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
     >
      Back to Pages
     </button>
    </div>
    <div className="bg-white shadow p-6">
     <FormSkeleton fields={6} showRichEditor={true} />
    </div>
   </div>
  );
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <h1 className="text-3xl font-bold">
     {slug && slug !== "new" ? "Edit Page" : "Create New Page"}
    </h1>
    <div className="flex gap-2">
     {slug && slug !== "new" && (
      <button
       onClick={() => setShowResetConfirm(true)}
       disabled={resettingZones}
       className="px-4 py-2 bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
       title="Reset all zones to default templates"
      >
       <FaUndo className="text-sm" />
       Reset Zones
      </button>
     )}
     <button
      onClick={() => navigate("/platform/pages")}
      className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
     >
      Back to Pages
     </button>
    </div>
   </div>

   {error && (
    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
     {error}
    </div>
   )}

   <div className="bg-white shadow p-6">
    <form onSubmit={handleSubmit} className="space-y-6">
     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Title <span className="text-red-500">*</span>
      </label>
      <input
       type="text"
       required
       value={formData.title}
       onChange={(e) => handleTitleChange(e.target.value)}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       placeholder={t("common.placeholders.pageTitle")}
      />
     </div>


     {/* Zone Management - Show for all pages that have zones */}
     {!isNewPage && hasZones() && (
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Page Zones
        {zoneNames.length > 0 && (
         <span className="ml-2 text-xs font-normal text-gray-500">
          (Drag to reorder)
         </span>
        )}
       </label>
       <div className="border border-gray-200 p-4">
        {zoneNames.length === 0 ? (
         <p className="text-sm text-gray-500 text-center py-4">
          No zones available. Zones will be created automatically when you save the page.
         </p>
        ) : (
         <ZoneList
          zoneNames={zoneNames}
          zones={zones}
          onEditZone={handleEditZone}
          onMoveZone={handleMoveZone}
          onDeleteZone={handleDeleteZone}
          onOrderChange={handleZoneOrderChange}
         />
        )}
       </div>
      </div>
     )}

     {/* Page Builder - Show for policy pages (which have both layout and zones) OR pages WITHOUT zones */}
     {/* Policy pages support both layout editing and zone management */}
     {/* Other pages with zones only show zone management, pages without zones only show page builder */}
     {(isPolicyPage(slug) || !hasZones()) && (
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Page Content
       </label>
       <div className="flex gap-2 mb-2">
        <button
         type="button"
         onClick={() => setShowPageBuilder(true)}
         className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
        >
         <FaEdit />
         {layout ? "Edit Page Builder" : "Open Page Builder"}
        </button>
        {layout && !showPageBuilder && (
         <span className="flex items-center text-sm text-green-600">
          ✓ Layout configured
         </span>
        )}
       </div>
       {showPageBuilder && (
        <PageBuilderProvider initialLayout={layout}>
         <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
          <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
           <h2 className="text-lg font-semibold">Edit Page Content</h2>
           <button
            onClick={() => setShowPageBuilder(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
           >
            <FaTimes />
            Close
           </button>
          </div>
          <div className="flex-1 overflow-hidden">
           <PageBuilder
            initialLayout={layout}
            onCancel={() => setShowPageBuilder(false)}
            onSave={async (newLayout) => {
             // For new pages, just update local state
             // For existing pages, save to database
             if (!slug || slug === "new") {
              setLayout(newLayout);
              setShowPageBuilder(false);
              return;
             }

             try {
              // For policy pages, preserve zones when saving layout
              const updateData: any = {
               ...formData,
               layout: newLayout,
              };
              
              // If this is a policy page and has zones, preserve them
              if (isPolicyPage(slug) && (zoneNames.length > 0 || Object.keys(zones).length > 0)) {
               const orderedZones: Record<string, IComponentNode> = {};
               zoneNames.forEach((zoneName) => {
                if (zones[zoneName]) {
                 orderedZones[zoneName] = zones[zoneName];
                }
               });
               updateData.zones = orderedZones;
               updateData.zoneNames = zoneNames;
              }
              
              const response = await platformApiService.updatePlatformPage(slug, updateData);
              if (response.data?.page?.layout) {
               setLayout(response.data.page.layout);
              } else {
               setLayout(newLayout);
              }
              setShowPageBuilder(false);
             } catch (error: any) {
              console.error("Error saving layout:", error);
              alert(error.response?.data?.error || "Failed to save page layout");
             }
            }}
            pageName={formData.title}
            pageType="platform"
           />
          </div>
         </div>
        </PageBuilderProvider>
       )}
       {!showPageBuilder && !layout && (
        <div className="border-2 border-dashed border-gray-300 p-8 text-center">
         <p className="text-gray-500 mb-2">No content configured</p>
         <p className="text-sm text-gray-400">Click "Open Page Builder" to start building your page</p>
        </div>
       )}
      </div>
     )}


     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Meta Title
      </label>
      <input
       type="text"
       value={formData.metaTitle}
       onChange={(e) =>
        setFormData({ ...formData, metaTitle: e.target.value })
       }
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       placeholder="SEO meta title"
      />
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Meta Description
      </label>
      <textarea
       value={formData.metaDescription}
       onChange={(e) =>
        setFormData({ ...formData, metaDescription: e.target.value })
       }
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       rows={3}
       placeholder="SEO meta description"
      />
     </div>

     <div>
      <label className="flex items-center gap-2">
       <input
        type="checkbox"
        checked={formData.isPublished}
        onChange={(e) =>
         setFormData({ ...formData, isPublished: e.target.checked })
        }
        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
       />
       <span className="text-sm font-medium text-gray-700">Published</span>
      </label>
      <p className="mt-1 text-sm text-gray-500">
       Published pages are visible to the public
      </p>
     </div>

     <div className="flex gap-2">
      <button
       type="submit"
       disabled={saving}
       className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
      >
       {saving ? "Saving..." : slug && slug !== "new" ? "Update Page" : "Create Page"}
      </button>
      <button
       type="button"
       onClick={() => navigate("/platform/pages")}
       className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
      >
       Cancel
      </button>
     </div>
    </form>
   </div>

   {/* Zone Editor Modal */}
   {editingZone && (
    <PageBuilderProvider initialLayout={editingZone.content}>
     <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
       <h2 className="text-lg font-semibold">
        Edit Zone: {editingZone.name}
       </h2>
       <button
        onClick={handleCancelZoneEdit}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
       >
        <FaTimes />
        Cancel
       </button>
      </div>
      <div className="flex-1 overflow-hidden">
       <PageBuilder
        initialLayout={editingZone.content}
        onCancel={handleCancelZoneEdit}
        onSave={(layout) => handleSaveZone(editingZone.name, layout)}
        zoneName={editingZone.name}
        pageName={formData.slug || slug || undefined}
        pageType="platform"
       />
      </div>
     </div>
    </PageBuilderProvider>
   )}

   {/* Reset Zones Confirmation Dialog */}
   {showResetConfirm && (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
     <div className="bg-white p-6 w-96 ">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
       Reset All Zones to Default Templates
      </h2>
      <p className="text-gray-600 mb-6">
       Are you sure you want to reset all zones for this page to their default templates? This will replace all current zone content and add any missing zones. This action cannot be undone.
      </p>
      <div className="flex justify-end space-x-4">
       <button
        onClick={() => setShowResetConfirm(false)}
        disabled={resettingZones}
        className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors disabled:opacity-50"
       >
        Cancel
       </button>
       <button
        onClick={handleResetZones}
        disabled={resettingZones}
        className="px-4 py-2 bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
       >
        {resettingZones ? (
         <>
          <Spinner size="sm" />
          Resetting...
         </>
        ) : (
         <>
          <FaUndo className="text-xs" />
          Reset Zones
         </>
        )}
       </button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
};

// Sortable Zone Item Component
interface SortableZoneItemProps {
 zoneName: string;
 zones: Record<string, IComponentNode>;
 onEdit: (zoneName: string) => void;
 onMove: (zoneName: string, direction: "up" | "down") => void;
 onDelete: (zoneName: string) => void;
 isFirst: boolean;
 isLast: boolean;
}

const SortableZoneItem: React.FC<SortableZoneItemProps> = ({ 
 zoneName, 
 onEdit, 
 onMove,
 onDelete,
 isFirst,
 isLast
}) => {
 const {
  attributes,
  listeners,
  setNodeRef,
  transform,
  transition,
  isDragging,
 } = useSortable({ id: zoneName });

 const style = {
  transform: CSS.Transform.toString(transform),
  transition,
  opacity: isDragging ? 0.5 : 1,
 };

 return (
  <div
   ref={setNodeRef}
   style={style}
   className={`flex items-center gap-3 p-3 bg-gray-50 border-2 ${
    isDragging ? "border-indigo-500" : "border-transparent"
   } hover:bg-gray-100 transition-colors`}
  >
   <div
    {...attributes}
    {...listeners}
    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
    title="Drag to reorder"
   >
    <FaGripVertical />
   </div>
   <span className="flex-1 text-sm font-medium text-gray-700 capitalize">
    {zoneName.replace(/-/g, " ")}
   </span>
   <div className="flex items-center gap-1">
    <button
     type="button"
     onClick={() => onMove(zoneName, "up")}
     disabled={isFirst}
     className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
     title="Move up"
    >
     <FaArrowUp />
    </button>
    <button
     type="button"
     onClick={() => onMove(zoneName, "down")}
     disabled={isLast}
     className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
     title="Move down"
    >
     <FaArrowDown />
    </button>
    <button
     type="button"
     onClick={() => onEdit(zoneName)}
     className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
    >
     <FaEdit />
     Edit
    </button>
    <button
     type="button"
     onClick={() => onDelete(zoneName)}
     className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
     title="Delete zone"
    >
     <FaTrash />
     Delete
    </button>
   </div>
  </div>
 );
};

// Zone List Component with Drag and Drop
interface ZoneListProps {
 zoneNames: string[];
 zones: Record<string, IComponentNode>;
 onEditZone: (zoneName: string) => void;
 onMoveZone: (zoneName: string, direction: "up" | "down") => void;
 onDeleteZone: (zoneName: string) => void;
 onOrderChange: (event: DragEndEvent) => void;
}

const ZoneList: React.FC<ZoneListProps> = ({ 
 zoneNames, 
 zones, 
 onEditZone, 
 onMoveZone,
 onDeleteZone,
 onOrderChange 
}) => {
 const sensors = useSensors(
  useSensor(PointerSensor),
  useSensor(KeyboardSensor, {
   coordinateGetter: sortableKeyboardCoordinates,
  })
 );

 return (
  <DndContext
   sensors={sensors}
   collisionDetection={closestCenter}
   onDragEnd={onOrderChange}
  >
   <SortableContext items={zoneNames} strategy={verticalListSortingStrategy}>
    <div className="space-y-2">
     {zoneNames.map((zoneName, index) => (
      <SortableZoneItem
       key={zoneName}
       zoneName={zoneName}
       zones={zones}
       onEdit={onEditZone}
       onMove={onMoveZone}
       onDelete={onDeleteZone}
       isFirst={index === 0}
       isLast={index === zoneNames.length - 1}
      />
     ))}
    </div>
   </SortableContext>
  </DndContext>
 );
};

export default PlatformPageForm;


